// SPDX-FileCopyrightText: 2024 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { nativeScrollViewProps } from 'components/common/scroll/TreactNativeScrollStyles';
import styled, { css } from 'styled-components';
import React, { useState } from 'react';
import { ThreeSectorContent } from '@tls/treact-ui-templates';
import { useParams } from 'react-router-dom';
import ParamsWithId from 'model/ParamsWithId';
import PreviewImage from './PreviewImage';
import LabelColorRect from './LabelColorRect';
import ProcessTag from 'components/common/ProcessTag';
import DataDetail from './detail/DataDetail';
import TagContainer from './TagContainer';
import DataElementOptions from './DataElementOptions';
import useNavigationFunctions from 'hooks/useNavigationFunctions';
import EditTagsDialog from './EditTagsDialog';
import useIsQC from 'hooks/useIsQC';
import { LabelPackageInfoWithUsedLabels } from './types';
import LabeledIndicator from './helper/LabeledIndicator';
import { ModelClassMessage } from '@tls/sw91-communication/types/com.base';
import { LabelPackageInfoMessage } from '@tls/sw91-communication/types/com.api_db_access';
import { Table } from '@tls/treact-ui';

const COLUMN_WIDTHS = { thumbnail: '72px', process: '72px', dataset: '56px', label: '50px', tags: '11rem' } as const;

interface Props {
  filteredLabels: LabelPackageInfoMessage[];
  projectTags: string[];
  modelClasses: ModelClassMessage[];
}

export default function DataTable({ filteredLabels, projectTags, modelClasses }: Readonly<Props>) {
  const isQC = useIsQC();
  const { id } = useParams<ParamsWithId>();

  const { navigateToLabel, navigateToAlgorithms } = useNavigationFunctions();
  const [tagDialogLabelPackage, setTagDialogLabelPackage] = useState<LabelPackageInfoMessage>();

  const labelClassesHeaderElements =
    modelClasses.map((modelClass, index) => (
      <HeaderSortCell
        isGroupEnd={index === (modelClasses.length ?? 0) - 1}
        key={'header_labelclass_' + modelClass.classIndex}
        className='compact-column'
        width={COLUMN_WIDTHS.label}
        sortKey={'labelClass_' + modelClass.classIndex}
        data-testid={`data-table-label-${modelClass.classIndex}-sort-button`}
        sortFn={(labels: LabelPackageInfoWithUsedLabels[]) => {
          return labels.sort((a, b) => {
            if (a.classIndicesInMask.includes(index + 1) && !b.classIndicesInMask.includes(index + 1)) {
              return -1;
            } else if (!a.classIndicesInMask.includes(index + 1) && b.classIndicesInMask.includes(index + 1)) {
              return 1;
            }
            return 0;
          });
        }}
      >
        <LabelColorRect size={'16px'} isUsedLabel labelColor={modelClasses[index].labelColor} />
      </HeaderSortCell>
    )) ?? [];

  const selectionAdapter = {
    menuLabel: 'Labels',
    tableHeaderCells: [
      <ThumbnailWrapper key='header_thumbnail' role='columnheader' />,
      <HeaderSortCell
        key='header_image'
        width={1}
        sortKey='image'
        data-testid='data-table-image-sort-button'
        sortFn={(labels: LabelPackageInfoMessage[]) => {
          return labels.sort((a, b) => {
            return a.imageName?.localeCompare(b.imageName ?? '') ?? 0;
          });
        }}
      >
        Image
      </HeaderSortCell>,
      isQC ? (
        <HeaderSortCell
          key='header_process_tag'
          className='compact-column'
          width={COLUMN_WIDTHS.process}
          sortKey='processTag'
          data-testid='data-table-process-tag-sort-button'
          sortFn={(labels: LabelPackageInfoMessage[]) => {
            return labels.sort((a, b) => {
              if (!a.hasMask && b.hasMask) {
                return 1;
              }
              return (a.qcResult ?? 0) > (b.qcResult ?? 0) ? -1 : 1;
            });
          }}
        >
          <span>
            Process
            <br />
            tag
          </span>
        </HeaderSortCell>
      ) : (
        <></>
      ),
      <HeaderSortCell
        isGroupEnd
        key='header_dataset'
        className='compact-column'
        width={COLUMN_WIDTHS.dataset}
        sortKey='dataset'
        data-testid='data-table-dataset-sort-button'
        sortFn={(labels: LabelPackageInfoMessage[]) => {
          return labels.sort((a, b) => {
            if (!a.hasMask && b.hasMask) {
              return 1;
            }
            if (a.hasMask && !b.hasMask) {
              return -1;
            }
            if (a.isValidation && !b.isValidation) {
              return -1;
            } else if (!a.isValidation && b.isValidation) {
              return 1;
            }
            return 0;
          });
        }}
      >
        <span>
          Data
          <br />
          set
        </span>
      </HeaderSortCell>,
      ...labelClassesHeaderElements,
      <HeaderCell key='header_tags' className='tags-column' width={COLUMN_WIDTHS.tags}>
        Tags
      </HeaderCell>,
    ],
    listElements: filteredLabels,
    listElementKeySelector: o => o.imageMd5!,
    renderTableRowCells: o => {
      return [
        <ThumbnailWrapper key='cell_thumbnail' data-testid={`data-image-${o.imageMd5 ?? 'unknown'}-preview`}>
          <PreviewImage previewData={o.previewData} />
        </ThumbnailWrapper>,
        <DataCell alignment='LEFT' key='cell_name' width={1} textOverflow='ellipsis'>
          {o.imageName}
        </DataCell>,
        isQC ? (
          <DataCell alignment='CENTER' key='cell_process_tag' className='compact-column' width={COLUMN_WIDTHS.process}>
            {o?.hasMask && o?.qcResult !== undefined ? (
              <ProcessTag processTag={o.qcResult} size={'24px'} />
            ) : (
              <div style={{ width: '24px' }} />
            )}
          </DataCell>
        ) : (
          <></>
        ),
        <DataCell
          alignment='CENTER'
          key='cell_data_tag'
          className='compact-column'
          width={COLUMN_WIDTHS.dataset}
          isGroupEnd
        >
          <LabeledIndicator
            hasMask={o.hasMask}
            isValidation={o.isValidation}
            isQC={isQC}
            iconLabelHeight='18px'
            circleStroke='4px'
            circleWidth='16px'
          />
        </DataCell>,
        ...(modelClasses.map((modelClass, index) => {
          return (
            <DataCell
              key={'cell_label_' + modelClass.classIndex}
              className='compact-column'
              alignment='CENTER'
              width={COLUMN_WIDTHS.label}
              isGroupEnd={index === (modelClasses?.length ?? 0) - 1}
            >
              <LabelColorRect
                size={'24px'}
                isUsedLabel={o.classIndicesInMask.includes(index + 1)}
                labelColor={modelClasses[index].labelColor}
                isLoading={false}
              />
            </DataCell>
          );
        }) ?? []),
        <DataCell key='cell_tags' className='tags-column' width={COLUMN_WIDTHS.tags}>
          <TagContent>
            <TagWrapper>
              <TagContainer withoutLabel withoutHideOverflow tags={o.tags} />
            </TagWrapper>
            <ActionWrapper>
              <DataElementOptions
                onOptionClick={id => {
                  if (id === 'editLabel') {
                    navigateToLabel(o.imageMd5);
                  } else if (id === 'editImageTags') {
                    setTagDialogLabelPackage(o);
                  } else if (id === 'editAlgorithms') {
                    navigateToAlgorithms(o.imageMd5);
                  }
                }}
              />
            </ActionWrapper>
          </TagContent>
        </DataCell>,
      ];
    },
    renderDetail: o => {
      if (o === undefined) return <></>;
      return (
        <DetailsWrapper data-testid={`data-image-${o.imageMd5 ?? 'unknown'}-detail`}>
          <DataDetail projectId={id} labelPackage={o} />
        </DetailsWrapper>
      );
    },
  };

  return (
    <Root>
      <ThreeSectorContent
        scrollViewProps={nativeScrollViewProps}
        sectorFlex={[0, 5, 3]}
        selectionAdapters={[selectionAdapter]}
      />
      {tagDialogLabelPackage && (
        <EditTagsDialog
          projectTags={projectTags}
          labelPackage={tagDialogLabelPackage}
          isOpen
          onClose={() => setTagDialogLabelPackage(undefined)}
        />
      )}
    </Root>
  );
}

const Root = styled.div`
  flex-grow: 1;
  min-width: 0;
  height: 0px;
`;

const ThumbnailWrapper = styled.div`
  flex: 0 1 ${COLUMN_WIDTHS.thumbnail};
  width: ${COLUMN_WIDTHS.thumbnail};
  min-width: 0;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const DetailsWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;
const TagWrapper = styled.div`
  flex-grow: 1;
  overflow: hidden;
  width: 0px;
`;
const TagContent = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  gap: 0.25rem;
`;
const ActionWrapper = styled.div`
  flex-shrink: 0;
`;

// Header and body must use identical outer widths and full-width inner containers.
// Table.Cell otherwise centers a content-sized container, including its divider.
const columnStyles = css<{ width: string | number; alignment?: 'LEFT' | 'CENTER' | 'RIGHT' }>`
  && {
    flex: ${props => (typeof props.width === 'number' ? `${props.width} 1 12rem` : `0 1 ${props.width}`)};
    min-width: 0;
    box-sizing: border-box;
  }

  &&.tags-column {
    min-width: 3.5rem;
  }

  && > div {
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    align-self: stretch;
    display: flex;
    align-items: center;
    justify-content: ${props => {
      if (props.alignment === 'CENTER') return 'center';
      if (props.alignment === 'RIGHT') return 'flex-end';
      return 'flex-start';
    }};
    padding: 0 0.5rem;
  }

  &&.compact-column > div {
    padding-inline: 0.25rem;
  }
`;
const DataCell = styled(Table.Cell)`
  ${columnStyles}

  && > div > span {
    min-width: 0;
  }
`;
const HeaderCell = styled(Table.HeaderCell)`
  ${columnStyles}
`;
const HeaderSortCell = styled(Table.HeaderSortCell)`
  ${columnStyles}

  && > div > button {
    min-width: 0;
    overflow: hidden;
  }

  && > div > button > svg {
    flex-shrink: 0;
    margin-left: 0;
  }
`;
